/**
 * Sifa Designs — Custom Product Page
 * Self-contained module (no theme imports). Integrates with the theme's
 * native cart via the documented `cart:update` event, which the theme's
 * cart drawer, cart icon and cart items components already listen for.
 */
(() => {
  'use strict';

  if (window.__cpxProductInit) return;
  window.__cpxProductInit = true;

  const CART_UPDATE_EVENT = 'cart:update';

  /* ------------------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------------- */

  // Section ids of the theme's cart components. Sent with cart/add.js so
  // Shopify returns their re-rendered HTML in the same response; passing
  // that HTML through the event lets the drawer morph synchronously
  // instead of racing an async section fetch while it opens.
  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  const dispatchCartUpdate = (cart, addedQty, variantId) => {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'cpx-product-form',
          data: {
            source: 'product-form-component',
            itemCount: addedQty,
            variantId: String(variantId ?? ''),
            sections: cart && cart.sections ? cart.sections : undefined,
          },
        },
      })
    );
  };

  const openCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) {
      drawer.open();
    }
  };

  /* ------------------------------------------------------------------
   * Main product component (gallery + variants + form + qty)
   * ---------------------------------------------------------------- */

  class CpxProduct {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.variants = this.readJson('[data-cpx-variants]') || [];
      this.selectedOptions = (this.readJson('[data-cpx-selected-options]') || []).slice();
      this.currentVariant = this.findVariant(this.selectedOptions) || this.variants[0] || null;

      this.gallery = root.querySelector('[data-cpx-gallery]');
      this.track = root.querySelector('[data-cpx-track]');
      this.form = root.querySelector('form[data-cpx-form]');
      this.qtyInput = root.querySelector('[data-cpx-qty-input]');
      this.errorEl = root.querySelector('[data-cpx-error]');
      this.stickyBar = document.querySelector(`[data-cpx-sticky="${this.sectionId}"]`);

      this.bindEvents();
      this.initGallery();
      this.initStickyBar();
    }

    readJson(selector) {
      const el = this.root.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch {
        return null;
      }
    }

    /* ---------------- events (delegated) ---------------- */

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const target = event.target;

        const variantBtn = target.closest('[data-cpx-option-value]');
        if (variantBtn) return this.onVariantClick(variantBtn);

        const qtyBtn = target.closest('[data-cpx-qty-change]');
        if (qtyBtn) return this.changeQty(Number(qtyBtn.dataset.cpxQtyChange));

        const thumb = target.closest('[data-cpx-thumb]');
        if (thumb) return this.goToSlide(Number(thumb.dataset.cpxThumb));

        const arrow = target.closest('[data-cpx-arrow]');
        if (arrow) return this.goToSlide(this.activeIndex() + Number(arrow.dataset.cpxArrow));

        const zoomBtn = target.closest('[data-cpx-zoom-open]');
        if (zoomBtn) return this.openLightbox();

        const copyBtn = target.closest('[data-cpx-copy]');
        if (copyBtn) return this.copyCode(copyBtn);

        const buyNow = target.closest('[data-cpx-buy-now]');
        if (buyNow) {
          event.preventDefault();
          return this.addToCart(buyNow, true);
        }

      });

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          const submitBtn = this.form.querySelector('[data-cpx-atc]');
          this.addToCart(submitBtn, false);
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.changeQty(0));
      }
    }

    /* ---------------- variants ---------------- */

    findVariant(options) {
      return this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
    }

    onVariantClick(btn) {
      const optionIndex = Number(btn.dataset.cpxOptionIndex);
      const value = btn.dataset.cpxOptionValue;
      if (this.selectedOptions[optionIndex] === value) return;

      this.selectedOptions[optionIndex] = value;
      let variant = this.findVariant(this.selectedOptions);

      // If the exact combination doesn't exist, snap remaining options to
      // the first variant that matches the newly picked value.
      if (!variant) {
        variant = this.variants.find((v) => v.options[optionIndex] === value);
        if (variant) this.selectedOptions = variant.options.slice();
      }

      this.currentVariant = variant || null;
      this.renderVariantState();
    }

    renderVariantState() {
      const variant = this.currentVariant;

      // Option buttons: pressed state + availability of each value given
      // the other currently selected options.
      this.root.querySelectorAll('[data-cpx-option-value]').forEach((btn) => {
        const idx = Number(btn.dataset.cpxOptionIndex);
        const value = btn.dataset.cpxOptionValue;
        btn.setAttribute('aria-pressed', this.selectedOptions[idx] === value ? 'true' : 'false');

        const hypothetical = this.selectedOptions.slice();
        hypothetical[idx] = value;
        const match = this.findVariant(hypothetical);
        btn.classList.toggle('cpx-is-unavailable', !match || !match.available);
      });

      // Selected value labels ("Diameter: 12 inch")
      this.root.querySelectorAll('[data-cpx-option-label]').forEach((label) => {
        const idx = Number(label.dataset.cpxOptionLabel);
        label.textContent = this.selectedOptions[idx] ?? '';
      });

      if (!variant) return this.renderAvailability(null);

      // Price
      this.root.querySelectorAll('[data-cpx-price]').forEach((el) => (el.textContent = variant.price_formatted));
      this.root.querySelectorAll('[data-cpx-compare]').forEach((el) => {
        const onSale = variant.compare_at > variant.price;
        el.hidden = !onSale;
        if (onSale) el.textContent = variant.compare_formatted;
      });
      this.root.querySelectorAll('[data-cpx-save]').forEach((el) => {
        const onSale = variant.compare_at > variant.price;
        el.hidden = !onSale;
        if (onSale) {
          const pct = Math.round(((variant.compare_at - variant.price) / variant.compare_at) * 100);
          el.textContent = el.dataset.cpxSaveTemplate.replace('[percent]', `${pct}%`);
        }
      });

      if (this.stickyBar) {
        const priceEl = this.stickyBar.querySelector('[data-cpx-sticky-price]');
        if (priceEl) priceEl.textContent = variant.price_formatted;
      }

      // SKU
      this.root.querySelectorAll('[data-cpx-sku]').forEach((el) => {
        el.hidden = !variant.sku;
        const val = el.querySelector('[data-cpx-sku-value]');
        if (val) val.textContent = variant.sku || '';
      });

      // Hidden form input + URL
      if (this.form) {
        const idInput = this.form.querySelector('input[name="id"]');
        if (idInput) idInput.value = variant.id;
      }
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }

      // Variant media
      if (variant.featured_media_index != null && variant.featured_media_index >= 0) {
        this.goToSlide(variant.featured_media_index);
      }

      this.renderAvailability(variant);
    }

    renderAvailability(variant) {
      const available = Boolean(variant && variant.available);
      const stockEls = this.root.querySelectorAll('[data-cpx-stock]');

      stockEls.forEach((el) => {
        const threshold = Number(el.dataset.cpxLowThreshold || 0);
        const qty = variant ? variant.inventory_quantity : 0;
        const tracked = variant ? variant.inventory_tracked : false;
        const low = available && tracked && qty > 0 && qty <= threshold;

        el.classList.toggle('cpx-stock--low', low);
        el.classList.toggle('cpx-stock--out', !available);

        const label = el.querySelector('[data-cpx-stock-label]');
        if (label) {
          if (!available) label.innerHTML = el.dataset.cpxOutText;
          else if (low) label.innerHTML = el.dataset.cpxLowText.replace('[count]', String(qty));
          else label.innerHTML = el.dataset.cpxInText;
        }
      });

      this.root.querySelectorAll('[data-cpx-atc]').forEach((btn) => {
        btn.disabled = !available;
        const label = btn.querySelector('[data-cpx-atc-label]');
        if (label) label.textContent = available ? btn.dataset.cpxAddText : btn.dataset.cpxSoldOutText;
      });
      this.root.querySelectorAll('[data-cpx-buy-now]').forEach((btn) => {
        btn.disabled = !available;
      });
      if (this.stickyBar) {
        const btn = this.stickyBar.querySelector('[data-cpx-sticky-atc]');
        if (btn) btn.disabled = !available;
      }
    }

    /* ---------------- quantity ---------------- */

    changeQty(delta) {
      if (!this.qtyInput) return;
      const min = Number(this.qtyInput.min || 1);
      let value = parseInt(this.qtyInput.value, 10);
      if (Number.isNaN(value)) value = min;
      value = Math.max(min, value + delta);
      const max = Number(this.qtyInput.max);
      if (max > 0) value = Math.min(value, max);
      this.qtyInput.value = String(value);
    }

    /* ---------------- cart ---------------- */

    async addToCart(button, redirectToCheckout) {
      if (!this.currentVariant || !this.currentVariant.available) return;
      const quantity = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1;

      if (button) button.classList.add('cpx-is-loading');
      this.showError('');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.currentVariant.id,
            quantity,
            sections: cartSectionIds().join(','),
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          this.showError(result.description || result.message || 'Could not add to cart.');
          return;
        }

        if (redirectToCheckout) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
          return;
        }

        dispatchCartUpdate(result, quantity, this.currentVariant.id);
        openCartDrawer();
      } catch {
        this.showError('Network error — please try again.');
      } finally {
        if (button) button.classList.remove('cpx-is-loading');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    /* ---------------- gallery ---------------- */

    initGallery() {
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-cpx-slide]'));
      this.thumbs = Array.from(this.root.querySelectorAll('[data-cpx-thumb]'));
      this.dots = Array.from(this.root.querySelectorAll('[data-cpx-dot]'));

      let scrollTimer;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => this.syncGalleryUi(), 80);
        },
        { passive: true }
      );

      this.root.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-cpx-gallery]')) return;
        if (event.key === 'ArrowLeft') this.goToSlide(this.activeIndex() - 1);
        if (event.key === 'ArrowRight') this.goToSlide(this.activeIndex() + 1);
      });

      // Start playback if the first slide is an autoplaying video, and
      // pause any playing gallery video while the tab is hidden.
      this.pauseInactiveVideos(this.activeIndex());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.track.querySelectorAll('video').forEach((v) => v.pause());
        } else {
          this.pauseInactiveVideos(this.activeIndex());
        }
      });
    }

    activeIndex() {
      if (!this.track || !this.slides.length) return 0;
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }

    goToSlide(index) {
      if (!this.track || !this.slides.length) return;
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: clamped * this.track.clientWidth, behavior: 'smooth' });
      this.syncGalleryUi(clamped);
    }

    syncGalleryUi(forceIndex) {
      const index = typeof forceIndex === 'number' ? forceIndex : this.activeIndex();
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      this.dots.forEach((d, i) => d.classList.toggle('cpx-is-active', i === index));
      const activeThumb = this.thumbs[index];
      if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.pauseInactiveVideos(index);
    }

    pauseInactiveVideos(activeIdx) {
      this.slides.forEach((slide, i) => {
        slide.querySelectorAll('video').forEach((v) => {
          if (i === activeIdx) {
            if (v.autoplay) {
              v.muted = true;
              v.playsInline = true;
              v.setAttribute('playsinline', '');
              const playing = v.play();
              if (playing) playing.catch(() => {});
            }
          } else {
            v.pause();
          }
        });
      });
    }

    openLightbox() {
      const dialog = this.root.querySelector('[data-cpx-lightbox]');
      if (!dialog) return;
      const img = dialog.querySelector('img');
      const slide = this.slides[this.activeIndex()];
      const source = slide && slide.querySelector('img');
      if (img && source) {
        img.src = source.currentSrc || source.src;
        img.alt = source.alt || '';
      }
      dialog.showModal();

      if (!dialog.__cpxBound) {
        dialog.__cpxBound = true;
        dialog.addEventListener('click', (event) => {
          if (event.target.closest('[data-cpx-lightbox-close]') || event.target === dialog) {
            dialog.close();
            return;
          }
          const wrap = event.target.closest('.cpx-lightbox__img-wrap');
          if (wrap) wrap.classList.toggle('cpx-is-zoomed');
        });
        dialog.addEventListener('close', () => {
          dialog.querySelector('.cpx-lightbox__img-wrap')?.classList.remove('cpx-is-zoomed');
        });
      }
    }

    /* ---------------- offer: copy discount code ---------------- */

    async copyCode(button) {
      const code = button.dataset.cpxCopy;
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        const temp = document.createElement('textarea');
        temp.value = code;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        temp.remove();
      }
      const original = button.textContent;
      button.textContent = button.dataset.cpxCopiedText || 'Copied!';
      button.classList.add('cpx-is-copied');
      setTimeout(() => {
        button.textContent = original;
        button.classList.remove('cpx-is-copied');
      }, 2000);
    }

    /* ---------------- sticky mobile ATC ---------------- */

    initStickyBar() {
      if (!this.stickyBar || !('IntersectionObserver' in window)) return;
      const sentinel = this.root.querySelector('[data-cpx-atc]') || this.form;
      if (!sentinel) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          this.stickyBar.classList.toggle(
            'cpx-is-visible',
            !entry.isIntersecting && entry.boundingClientRect.top < 0
          );
        },
        { threshold: 0 }
      );
      observer.observe(sentinel);

      const btn = this.stickyBar.querySelector('[data-cpx-sticky-atc]');
      if (btn) btn.addEventListener('click', () => this.addToCart(btn, false));
    }
  }

  /* ------------------------------------------------------------------
   * Details tabs (desktop tabs / mobile accordion)
   * ---------------------------------------------------------------- */

  const initDetails = (root) => {
    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-cpx-tab]');
      if (tab) {
        const id = tab.dataset.cpxTab;
        root.querySelectorAll('[data-cpx-tab]').forEach((t) => t.setAttribute('aria-selected', t.dataset.cpxTab === id ? 'true' : 'false'));
        root.querySelectorAll('[data-cpx-panel]').forEach((p) => (p.hidden = p.dataset.cpxPanel !== id));
        return;
      }

      const accHead = event.target.closest('[data-cpx-acc]');
      if (accHead) {
        const id = accHead.dataset.cpxAcc;
        const panel = root.querySelector(`[data-cpx-panel="${id}"]`);
        const expanded = accHead.getAttribute('aria-expanded') === 'true';
        // Accordion: collapse the others.
        root.querySelectorAll('[data-cpx-acc]').forEach((h) => h.setAttribute('aria-expanded', 'false'));
        root.querySelectorAll('[data-cpx-panel]').forEach((p) => (p.hidden = true));
        if (!expanded && panel) {
          accHead.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      }
    });
  };

  /* ------------------------------------------------------------------
   * Recommendations (Section Rendering API)
   * ---------------------------------------------------------------- */

  const initRecommendations = (root) => {
    const url = root.dataset.cpxRecsUrl;
    if (!url) return;

    const load = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const inner = html.querySelector('[data-cpx-recs-inner]');
        if (inner && inner.children.length > 0) {
          root.querySelector('[data-cpx-recs-inner]').replaceWith(inner);
        } else {
          root.hidden = true;
        }
      } catch {
        root.hidden = true;
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(root);
    } else {
      load();
    }
  };

  /* ------------------------------------------------------------------
   * Upsell-style product cards (info-column slider + recommendations)
   * Document-level delegation so cards keep working wherever they
   * render, including content swapped in via the Section Rendering API.
   * ---------------------------------------------------------------- */

  const scrollUpsell = (arrow) => {
    const wrap = arrow.closest('[data-cpx-upsell]');
    const track = wrap && wrap.querySelector('[data-cpx-upsell-track]');
    if (!track) return;
    const card = track.querySelector('[data-cpx-upsell-card]');
    const step = card ? card.getBoundingClientRect().width + 12 : track.clientWidth;
    track.scrollBy({ left: step * Number(arrow.dataset.cpxUpsellArrow), behavior: 'smooth' });
  };

  const onUpsellSelect = (select) => {
    const card = select.closest('[data-cpx-upsell-card]');
    if (!card) return;
    const option = select.selectedOptions[0];
    const priceEl = card.querySelector('[data-cpx-upsell-price]');
    const btn = card.querySelector('[data-cpx-upsell-atc]');
    if (priceEl && option) {
      const compare = option.dataset.compare;
      priceEl.innerHTML = `${option.dataset.price || ''}${compare ? ` <s>${compare}</s>` : ''}`;
    }
    if (btn && option) {
      btn.dataset.variantId = option.value;
      btn.disabled = option.disabled || option.dataset.available === 'false';
    }
  };

  const addUpsell = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    button.classList.add('cpx-is-loading');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: cartSectionIds().join(','),
        }),
      });
      const result = await response.json();
      if (!response.ok) return;

      dispatchCartUpdate(result, 1, variantId);
      openCartDrawer();

      const original = button.textContent;
      button.textContent = button.dataset.addedText || 'Added';
      button.classList.add('cpx-is-added');
      setTimeout(() => {
        button.textContent = original;
        button.classList.remove('cpx-is-added');
      }, 2000);
    } catch {
      /* network error — leave the button usable for a retry */
    } finally {
      button.classList.remove('cpx-is-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-cpx-upsell-arrow]');
    if (arrow) return scrollUpsell(arrow);

    const atc = event.target.closest('[data-cpx-upsell-atc]');
    if (atc) addUpsell(atc);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cpx-upsell-select]');
    if (select) onUpsellSelect(select);
  });

  /* ------------------------------------------------------------------
   * Video gallery slider — arrows + play-when-visible
   * ---------------------------------------------------------------- */

  const initVideoGallery = (root) => {
    const track = root.querySelector('[data-cpx-videogal-track]');
    if (!track) return;

    root.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-cpx-videogal-arrow]');
      if (!arrow) return;
      const card = track.querySelector('[data-cpx-videogal-card]');
      const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
      track.scrollBy({ left: step * Number(arrow.dataset.cpxVideogalArrow), behavior: 'smooth' });
    });

    if (root.dataset.cpxVgAutoplay !== 'true') return;

    const playVideo = (video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      const playing = video.play();
      if (playing) playing.catch(() => {});
    };

    const videos = track.querySelectorAll('video');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && !document.hidden) playVideo(video);
            else video.pause();
          });
        },
        { threshold: 0.35 }
      );
      videos.forEach((v) => observer.observe(v));
    } else {
      videos.forEach(playVideo);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach((v) => v.pause());
    });
  };

  /* ------------------------------------------------------------------
   * Boot
   * ---------------------------------------------------------------- */

  const boot = () => {
    document.querySelectorAll('[data-cpx-product]').forEach((el) => {
      if (!el.__cpxInstance) el.__cpxInstance = new CpxProduct(el);
    });
    document.querySelectorAll('[data-cpx-details]').forEach((el) => {
      if (!el.__cpxInit) {
        el.__cpxInit = true;
        initDetails(el);
      }
    });
    document.querySelectorAll('[data-cpx-recs]').forEach((el) => {
      if (!el.__cpxInit) {
        el.__cpxInit = true;
        initRecommendations(el);
      }
    });
    document.querySelectorAll('[data-cpx-videogal]').forEach((el) => {
      if (!el.__cpxInit) {
        el.__cpxInit = true;
        initVideoGallery(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-boot when sections are re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
